import type { IProcessStepScreenLink } from "./designer-workflow-start-params";

/**
 * Structural subset of the project that the managed-task-screen predicate
 * reads. Deliberately minimal so every caller — the MCP screen tools, the
 * chat context builder, the action-definition filter — can pass its own
 * loosely-typed project object without a cast.
 */
export interface IManagedTaskScreenProjectLike {
  processStepScreenIds?: IProcessStepScreenLink[];
}

/**
 * The workflow user-task step a managed screen is generated for. Both ids are
 * optional because a link may carry only the screen side; membership does not
 * depend on them (see `findManagedTaskScreenLink`).
 */
export interface IManagedTaskScreenLink {
  /** The workflow (BPMN process) id — what `navigate_to_workflow` takes. */
  readonly processId?: string;
  /** The user-task step id — what a `SCREEN_FIELD` / `ASSIGN_SCREEN` ref addresses. */
  readonly stepId?: string;
}

/**
 * True when `screenId` is a PLATFORM-MANAGED workflow task screen — the
 * project links it to a workflow user-task step through
 * `project.processStepScreenIds`.
 *
 * Why this predicate is worth a shared module: a managed task screen's FORM
 * is generated from workflow form metadata (`ICRUDFormDiMetadata`) and is
 * REGENERATED on every `UpdateWorkflowScreens`. Backend
 * `FormBuilderService.ApplyUserChangedPropertiesToFormDi` decides what
 * survives that regeneration, and the split is not symmetric:
 *
 *   - PRESERVED (`di.StyleObj`, `Authorization`, `Editability`,
 *     `Item.Validations`, `FriendlyName`) → legitimately Designer-owned.
 *   - RE-IMPOSED FROM METADATA — a `Label`'s `value` is rewritten from
 *     `ffield.Name`, and a field cell `Col`'s responsive spans are rewritten
 *     from `section.ResponsiveLayoutSizes` → these are DSL-owned
 *     (`SCREEN_FIELD label:` / `SCREEN_SECTION columns:`).
 *
 * So a tree-level patch of a form label or field-cell width on a managed
 * screen is not merely redundant, it is EPHEMERAL: it silently reverts the
 * next time the workflow is touched. Callers use this predicate to route
 * such edits to `ui_workflow_dsl` instead of hand-patching the tree.
 *
 * Membership is compared as a string on both sides because
 * `processStepScreenIds[].screenId` has historically arrived as either a
 * string or a Guid-like object (the pre-existing call sites all carried a
 * `?.toString()` fallback).
 */
export function isManagedTaskScreenId(
  project: IManagedTaskScreenProjectLike | null | undefined,
  screenId: string | null | undefined
): boolean {
  return findManagedTaskScreenLink(project, screenId) !== null;
}

/**
 * The workflow step a managed task screen belongs to — `{ processId, stepId }` —
 * or null when `screenId` is not a managed task screen.
 *
 * This is the identity half of the same lookup `isManagedTaskScreenId` performs,
 * and it is load-bearing for the DSL redirect the validator issues.
 *
 * KOA-17362 follow-up (live smoke chat_1785187254829_9ssyua). Telling the planner
 * "author it as `ui_workflow_dsl` → `SCREEN_FIELD <step> …`" is useless when it
 * knows neither WHICH workflow nor WHICH step: it emitted a bare `LIST` to
 * discover them, `ui_workflow_dsl` aborted with "no workflow is currently
 * selected" (the user is on a SCREEN, so no BPMN canvas is mounted), the A1 plan
 * halted at step 0, and the turn degraded into a brief-less Designer that spent
 * 20 rounds attempting the very form patches the gate had just refused.
 *
 * The cure is not a bigger prompt — it is shipping the identity that is already
 * sitting in the same array element. With `processId` in hand the planner can
 * satisfy the DSL's precondition itself
 * (`ui_project_extended.navigate_to_workflow`), and with `stepId` it can address
 * the step instead of a `<step>` placeholder.
 */
export function findManagedTaskScreenLink(
  project: IManagedTaskScreenProjectLike | null | undefined,
  screenId: string | null | undefined
): IManagedTaskScreenLink | null {
  if (!project) return null;
  if (typeof screenId !== "string" || screenId.length === 0) return null;
  const links: IProcessStepScreenLink[] | undefined = project.processStepScreenIds;
  if (!Array.isArray(links)) return null;
  for (const link of links) {
    if (asLinkId(link?.screenId) !== screenId) continue;
    const processId: string = asLinkId(link?.processId);
    const stepId: string = asLinkId(link?.stepId);
    // A link with no step identity still PROVES managed membership (the
    // regeneration risk is real either way), so the predicate must stay true;
    // the ids are reported only when actually present.
    return {
      ...(processId.length > 0 ? { processId } : {}),
      ...(stepId.length > 0 ? { stepId } : {})
    };
  }
  return null;
}

/**
 * The managed screen a workflow STEP owns — the inverse of
 * {@link findManagedTaskScreenLink} — or null when that step has none.
 *
 * KOA-17362 §25. `ASSIGN_SCREEN`'s outcome used to be observable ONLY through its
 * own dispatch response: the orchestrator's deterministic backstop parsed
 * `assignedScreens` out of the reply and, when the reply did not parse (a 90s
 * transport timeout while the browser kept working — `ASSIGN_SCREEN` generates a
 * form screen per user task and was measured at ~60s for two), reported
 * "expected=2 landed=0" even though the screens existed. A late success is
 * indistinguishable from a failure unless the STATE can be asked, and the state
 * has always been right here in the same array.
 *
 * Also the natural read for "does this task already have a screen?", which is why
 * `listDesignerWorkflowSteps` now surfaces it per element.
 */
export function findManagedScreenIdForStep(
  project: IManagedTaskScreenProjectLike | null | undefined,
  processId: string | null | undefined,
  stepId: string | null | undefined
): string | null {
  if (!project) return null;
  if (typeof processId !== "string" || processId.length === 0) return null;
  if (typeof stepId !== "string" || stepId.length === 0) return null;
  const links: IProcessStepScreenLink[] | undefined = project.processStepScreenIds;
  if (!Array.isArray(links)) return null;
  for (const link of links) {
    if (asLinkId(link?.processId) !== processId) continue;
    if (asLinkId(link?.stepId) !== stepId) continue;
    const screenId: string = asLinkId(link?.screenId);
    // A link with no screen id proves membership but names no screen — the
    // caller needs a screen, so keep looking rather than answering with "".
    if (screenId.length > 0) return screenId;
  }
  return null;
}

/**
 * Normalize one id field of a link. `processStepScreenIds` entries have
 * historically arrived with either strings or Guid-like objects, which is why
 * every pre-existing call site carried a `?.toString()` fallback.
 */
function asLinkId(value: unknown): string {
  if (value === undefined || value === null) return "";
  return typeof value === "string" ? value : String(value);
}

/**
 * KOA-18136 §58, re-homed by KOA-18431 — THE ENGINE'S PROCESS ACTIONS ARE NOT ON A TASK SCREEN.
 *
 * ## What it refuses
 *
 * A task screen is the ASSIGNEE's form for the step they are holding. It has no business listing
 * other people's pending tasks, reading the engine's admin view of the instance, or driving
 * instance lifecycle. Live `b3bd8530` bound `GetProcessData` — an ADMIN read served under the
 * `WorkflowAdmin` policy with NO per-user ownership check — into one approval screen, and
 * `GetProcessDetails.Steps` into another, where the columns paired to real field names and the
 * values never arrived. Both looked wired and both were wrong.
 *
 * ## Why it lives here now
 *
 * The original enforcement hid this family from the Designer's action MENU on the managed-screen
 * function turn. That turn is gone (the workflow DSL composes these screens), and with it the only
 * way an LLM could pick one of these off a menu. What replaced it can still NAME one: the workflow
 * builder writes `ACTION #alias <event> "<Name>"` lines by hand. So the law moved to the surface
 * that can still break it, and it is enforced where the line is applied rather than taught in a
 * prompt — an LLM's restraint is not a guard.
 *
 * Everything a process legitimately reads on a screen is bound by the process-screen projection on
 * the screens that carry a ROLE — an inbox, a status page. Those are not task screens.
 *
 * Exact name matching, case-insensitively: these are platform-owned identifiers, never
 * user-authored, so no stem or prefix matching is warranted. A customer action legitimately called
 * `ClaimTaskNote` or `GetProcessDataArchive` must not disappear.
 */
const ENGINE_PROCESS_ACTION_NAMES: readonly string[] = [
  // Instance lifecycle — the engine drives it, a screen does not.
  "StartProcess",
  "ResumeProcess",
  "SuspendProcessInstance",
  "RetryProcessInstance",
  "TerminateProcessInstance",
  // Task assignment / ownership.
  "ClaimTask",
  "ReleaseTaskToPool",
  "AssignTaskToUser",
  "DynamicAssignment",
  "SetManager",
  "FindManager",
  "SetAgent",
  "GetAgent",
  // Task + instance reads, including the admin ones.
  "GetMyTasksInfo",
  "GetMyTasksHistory",
  "GetUserTasks",
  "GetTaskDetails",
  "GetProcessData",
  "GetProcessInstance",
  "GetProcessInstances",
  "GetProcessSteps",
  "GetProcessDetails",
  "GetProcessStatusOptions",
  "GetProcessRegistryList",
  "GetProcessesStartedByUser",
  "GetProcessHappyPathSummary",
  // Navigation INTO a managed task screen — from a task screen, meaningless.
  "NavigateToTask",
  // Step payload persistence — engine-owned; the generated buttons already do it.
  "SaveStepData"
];

const ENGINE_PROCESS_ACTION_LOOKUP: ReadonlySet<string> = new Set<string>(
  ENGINE_PROCESS_ACTION_NAMES.map((name: string): string => name.toLowerCase())
);

/** True when `name` is one of the engine's own process actions (exact, case-insensitive). */
export function isEngineProcessAction(name: string): boolean {
  return ENGINE_PROCESS_ACTION_LOOKUP.has(name.trim().toLowerCase());
}
